import React, { useMemo, useState } from 'react';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Labor/EditWorkerModal.css';
import { useDispatch } from 'react-redux';
import { updateWorker, fetchWorkers, refreshLaborProject } from '../../../../../../../Redux/Projects/Labor/laborSlice';
import { showFeedback } from '../../../../../../../Redux/UI/uiFeedbackSlice';

const EditWorkerModal = ({ worker, projectId, onClose }) => {
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: worker?.firstName || '',
    lastName: worker?.lastName || '',
    phone: worker?.phone || '',
    wages: worker?.wages ?? '',
    role: worker?.role || 'Worker',
  });

  const phoneError = useMemo(() => {
    const digits = String(formData.phone || '').replace(/\D/g, '');
    if (!digits) return 'Phone is required';
    if (digits.length !== 10) return 'Phone must be exactly 10 digits';
    return '';
  }, [formData.phone]);

  const wagesError = useMemo(() => {
    const n = Number(formData.wages);
    if (!formData.wages && formData.wages !== 0) return 'Wages are required';
    if (!Number.isFinite(n) || n <= 0) return 'Wages must be > 0';
    return '';
  }, [formData.wages]);

  const nameError = useMemo(() => {
    if (!String(formData.firstName || '').trim()) return 'First name is required';
    return '';
  }, [formData.firstName]);

  const canSave = !nameError && !phoneError && !wagesError && !isSaving;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setFormData((p) => ({ ...p, phone: digits }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    if (!projectId) {
      dispatch(showFeedback({ type: 'error', title: 'No project', message: 'Project id missing. Close and retry.' }));
      return;
    }

    // hard stop
    if (!canSave) {
      const msg = nameError || phoneError || wagesError || 'Fix errors before saving.';
      dispatch(showFeedback({ type: 'error', title: 'Invalid input', message: msg }));
      return;
    }

    const updates = {
      firstName: String(formData.firstName || '').trim(),
      lastName: String(formData.lastName || '').trim(),
      phone: String(formData.phone || '').trim(),
      role: String(formData.role || '').trim(),
      wages: Number(formData.wages),
    };

    try {
      setIsSaving(true);

      const action = await dispatch(updateWorker({ workerId: worker._id, updates }));

      // if your thunk supports matcher:
      if (updateWorker.rejected?.match?.(action)) {
        const msg = action.payload?.message || action.error?.message || 'Failed to update worker.';
        dispatch(showFeedback({ type: 'error', title: 'Update failed', message: msg }));
        return;
      }

      // Prefer the same "one thunk refreshes all" pattern you used in Labor
      await dispatch(refreshLaborProject(projectId));

      dispatch(showFeedback({ type: 'success', title: 'Worker updated', message: 'Changes saved successfully.' }));
      onClose();
    } catch (err) {
      console.error(err);
      dispatch(showFeedback({ type: 'error', title: 'Update failed', message: err?.message || 'Something went wrong.' }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="edit-worker-modal-overlay" onMouseDown={onClose} role="dialog" aria-modal="true">
      <div className="edit-worker-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="edit-worker-modal-header">
          <h3>Edit Worker</h3>
          <button onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="edit-worker-modal-form">
          <label>First Name</label>
          <input name="firstName" value={formData.firstName} onChange={handleChange} />
          {nameError && <div className="input-error">{nameError}</div>}

          <label>Last Name</label>
          <input name="lastName" value={formData.lastName} onChange={handleChange} />

          <label>Phone</label>
          <input name="phone" value={formData.phone} onChange={handleChange} inputMode="numeric" />
          {phoneError && <div className="input-error">{phoneError}</div>}

          <label>Role</label>
          <input name="role" value={formData.role} onChange={handleChange} />

          <label>Wages (₹/day)</label>
          <input name="wages" type="number" value={formData.wages} onChange={handleChange} />
          {wagesError && <div className="input-error">{wagesError}</div>}
        </div>

        <div className="edit-worker-modal-actions">
          <button className="btn black" onClick={handleUpdate} disabled={!canSave}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
          <button className="btn cancel" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditWorkerModal;
